/**
 * Registering THIS BROWSER with the push service, and taking it back off.
 *
 * The browser is just another FCM device: Firebase's web SDK mints an FCM
 * registration token against our VAPID key, and the push service stores and
 * sends to it exactly as it does a phone's (`transport: 'fcm'`,
 * `platform: 'web'`). Nothing about delivery is new — the service builds the
 * `webpush` block, and the service worker at `/push/sw.js` shows what arrives.
 *
 * ### Two locks, both must be open
 * - Client: the PostHog experiment `web-push-notifications`
 *   (`use-web-push-flag.ts`). Only the `test` variant registers; everyone else,
 *   and every browser without a Firebase config, never touches any of this.
 * - Server: `PUSH_WEB` on the push service. While it is off the service
 *   refuses a browser's registration (403 `web-push-disabled`) and leaves
 *   browsers out of every send, so a client-side mistake cannot reach anyone.
 *
 * ### Its own service worker scope
 * The PWA plugin registers `/sw.js` at scope `/` in self-destroying mode — it
 * unregisters itself on every load. A push subscription belongs to its service
 * worker registration, so sharing that scope would kill the subscription with
 * it. The push worker lives at `/push/`, which nothing else claims.
 *
 * ### Permission is the user's
 * Nothing here prompts unless the caller says the user just asked for it
 * (`prompt: true`): the "Enable" of the dashboard's own notification sheet
 * (`components/push/web-push-prompt.tsx`), or Settings' "This browser → Allow".
 * Browsers remember a dismissed prompt and some block a site that asks on page
 * load, so the native prompt never appears unasked.
 */

import { PUSH_SERVICE_PATHS, PUSH_SIGN_OUT_BUDGET_MS } from '@flashka/shared/push/types';

import i18n from '@/i18n';

import { type WebPushConfig, webPushConfig } from './web-push-config';

export const WEB_PUSH_SW_URL = '/push/sw.js';
export const WEB_PUSH_SW_SCOPE = '/push/';

/** One id per browser profile — the push service keys a device by it. */
export const WEB_PUSH_INSTALLATION_ID_KEY = 'flashka.push.installationId';
/**
 * "This browser holds a push subscription we may have to take back" — set the
 * moment a subscription exists (before the service is even asked), so that
 * nothing a failed registration leaves behind is ever orphaned. Its absence is
 * what lets a browser that never subscribed skip the unregister request.
 */
export const WEB_PUSH_REGISTERED_USER_KEY = 'flashka.push.registeredUser';
/** `{ userId, at }` of the last registration the service ACCEPTED. */
export const WEB_PUSH_LAST_REGISTERED_KEY = 'flashka.push.lastRegistered';
/** Per tab session: the service said browsers are switched off (`PUSH_WEB`). */
const WEB_PUSH_SERVER_DISABLED_KEY = 'flashka.push.serverDisabled';
/**
 * `{ userId, reason, at }` of the service's last REFUSAL of this browser
 * (`refused`, `server-disabled`). Page loads do not ask again for
 * `WEB_PUSH_REFUSAL_BACKOFF_MS` — each attempt downloads Firebase, mints a
 * token, and ends it again. The user asking (Settings, the sheet) still does.
 */
export const WEB_PUSH_REFUSED_KEY = 'flashka.push.refused';
export const WEB_PUSH_REFUSAL_BACKOFF_MS = 24 * 60 * 60 * 1000;
/** How long a newly registered push worker may take to activate. */
export const WEB_PUSH_SW_ACTIVATE_TIMEOUT_MS = 10_000;
/**
 * How often an unchanged browser re-registers. The service short-circuits an
 * unchanged device anyway; this spares the Firebase download and two round
 * trips on every page load. Well inside the service's 270-day staleness sweep
 * and Firebase's own weekly token refresh.
 */
export const WEB_PUSH_REFRESH_MS = 24 * 60 * 60 * 1000;

/** The push service's answer while browsers are switched off server-side. */
const WEB_PUSH_DISABLED_ERROR = 'web-push-disabled';

export type WebPushRefusal =
  /** No service worker / Push API / Notification API, or no Firebase config. */
  | 'unsupported'
  /** Permission is not granted — not asked yet, dismissed, or denied. */
  | 'no-permission'
  /** Firebase could not mint a token (blocked storage, a revoked key…). */
  | 'no-token'
  /** No session. */
  | 'signed-out'
  /** The server's own switch (`PUSH_WEB`) is off. Page loads leave it a day. */
  | 'server-disabled'
  /** Asked and got no usable answer: offline, timeout, or a 5xx. */
  | 'unreachable'
  /** Any other 4xx. Never retried blindly: page loads leave it a day. */
  | 'refused';

/**
 * `fresh`: registered recently for this user, so nothing was done.
 * `joined`: another call was already registering this browser; this one
 * waited for it and reports ITS outcome — the work (and its analytics) is
 * that call's, not this one's.
 */
export type WebPushResult =
  | { ok: true; fresh?: boolean; joined?: boolean }
  | { ok: false; reason: WebPushRefusal; joined?: boolean };

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function read(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function session(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** A sessionStorage write that cannot throw — blocked storage costs a retry. */
function writeSession(key: string, value: string | null): void {
  try {
    const s = session();
    if (!s) return;
    if (value === null) s.removeItem(key);
    else s.setItem(key, value);
  } catch {
    // Blocked storage: the worst case is one extra request.
  }
}

/** The service's refusal of `userId` on this browser, while it still holds. */
function recentRefusal(userId: string): WebPushRefusal | null {
  try {
    const parsed = JSON.parse(read(WEB_PUSH_REFUSED_KEY) ?? 'null') as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const refusal = parsed as { userId?: unknown; reason?: unknown; at?: unknown };
    const { reason, at } = refusal;
    if (refusal.userId !== userId || typeof at !== 'number') return null;
    if (reason !== 'refused' && reason !== 'server-disabled') return null;
    const age = Date.now() - at;
    return age >= 0 && age < WEB_PUSH_REFUSAL_BACKOFF_MS ? reason : null;
  } catch {
    return null;
  }
}

function lastRegistered(): { userId: string; at: number } | null {
  try {
    const parsed = JSON.parse(read(WEB_PUSH_LAST_REGISTERED_KEY) ?? 'null') as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const { userId, at } = parsed as { userId?: unknown; at?: unknown };
    return typeof userId === 'string' && typeof at === 'number' ? { userId, at } : null;
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    const s = storage();
    if (!s) return;
    if (value === null) s.removeItem(key);
    else s.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the worst case is one extra request.
  }
}

/** Can this browser receive a push at all? Never prompts, never throws. */
export function browserSupportsWebPush(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return (
    window.isSecureContext !== false &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Web push is POSSIBLE here: a capable browser and a configured build. */
export function webPushAvailable(): boolean {
  return browserSupportsWebPush() && webPushConfig() !== null;
}

export type WebPushPermission = NotificationPermission | 'unsupported';

export function webPushPermission(): WebPushPermission {
  if (!browserSupportsWebPush()) return 'unsupported';
  return Notification.permission;
}

/**
 * Did the service refuse this browser this session because browsers are
 * switched off (`PUSH_WEB`)? Settings says so rather than implying the browser
 * is covered.
 */
export function webPushServerRefused(): boolean {
  try {
    return session()?.getItem(WEB_PUSH_SERVER_DISABLED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Does this browser hold a registration we would need to take back? */
export function hasWebPushRegistration(): boolean {
  return webPushRegisteredUserId() !== null;
}

/** Whose registration this browser holds — not necessarily the signed-in user's. */
export function webPushRegisteredUserId(): string | null {
  return read(WEB_PUSH_REGISTERED_USER_KEY);
}

/**
 * What this browser's registration should do on this load.
 *
 * - `on` + permission granted → register (at most daily — `registerWebPush`
 *   skips a fresh one). Never prompts: that is the user's to ask for.
 * - `on` but permission revoked since → take the registration back.
 * - `off` → take it back too: the experiment ended, moved this user to
 *   `control`, or nobody is signed in any more.
 * - `unknown` (flags not loaded yet) → nothing. Reading that as `off` would
 *   unregister every enrolled browser on every page load.
 *
 * "Take it back" only when there is something to take: a browser that never
 * registered costs nobody a request.
 */
export function webPushAction(state: {
  flag: 'unknown' | 'on' | 'off';
  signedIn: boolean;
  permission: WebPushPermission;
  registered: boolean;
}): 'register' | 'unregister' | 'none' {
  if (state.flag === 'unknown') return 'none';
  if (state.flag === 'on' && state.signedIn && state.permission === 'granted')
    return 'register';
  return state.registered ? 'unregister' : 'none';
}

/** This browser's installation id, minted on first use — also what its
 *  notifications-screen receipts are filed under (`push-open.ts`). */
export function webPushInstallationId(): string {
  return installationId();
}

function installationId(): string {
  const existing = read(WEB_PUSH_INSTALLATION_ID_KEY);
  if (existing) return existing;
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  write(WEB_PUSH_INSTALLATION_ID_KEY, id);
  return id;
}

/** Same-origin: `server/routes/auth-push.ts` forwards with the caller's cookie. */
async function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

async function refusalFor(res: Response): Promise<WebPushRefusal> {
  if (res.status === 401) return 'signed-out';
  if (res.status >= 500) return 'unreachable';
  if (res.status === 403) {
    const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
    if (body?.error === WEB_PUSH_DISABLED_ERROR) return 'server-disabled';
  }
  return 'refused';
}

async function messagingFor(config: WebPushConfig) {
  // Loaded on demand: only a browser in the experiment ever downloads Firebase.
  const [{ getApps, initializeApp }, messagingModule] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);
  if (!(await messagingModule.isSupported())) return null;
  const app =
    getApps().find((a) => a.name === 'flashka-web-push') ??
    initializeApp(config.firebase, 'flashka-web-push');
  return { module: messagingModule, messaging: messagingModule.getMessaging(app) };
}

/**
 * The push worker's registration, once its worker is ACTIVE. `register()`
 * resolves while a first-time worker is still installing, and Firebase skips
 * its own wait when handed a registration — `pushManager.subscribe` then
 * rejects for want of an active worker. Firebase's `waitForRegistrationActive`,
 * for our registration.
 */
async function pushWorker(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register(WEB_PUSH_SW_URL, {
    scope: WEB_PUSH_SW_SCOPE,
  });
  if (registration.active) return registration;
  const incoming = registration.installing ?? registration.waiting;
  if (!incoming) throw new Error('No incoming push worker');
  await new Promise<void>((resolve, reject) => {
    const done = () => {
      clearTimeout(timer);
      incoming.removeEventListener('statechange', onStateChange);
    };
    const onStateChange = () => {
      if (incoming.state === 'activated') {
        done();
        resolve();
      } else if (incoming.state === 'redundant') {
        done();
        reject(new Error('Push worker became redundant'));
      }
    };
    const timer = setTimeout(() => {
      done();
      reject(new Error('Push worker did not activate'));
    }, WEB_PUSH_SW_ACTIVATE_TIMEOUT_MS);
    incoming.addEventListener('statechange', onStateChange);
    // It may have activated between `register()` resolving and now.
    onStateChange();
  });
  return registration;
}

/**
 * Does the push worker still hold a subscription? A "fresh" registration
 * means nothing without one — the browser can drop it (storage cleared, the
 * push service rotated it), and only a new registration makes a new one.
 */
async function holdsSubscription(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.getRegistration(WEB_PUSH_SW_SCOPE);
    return Boolean(await registration?.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/**
 * End this browser's subscription and its worker. The FCM token IS the
 * subscription: ending it is what kills the token. No Firebase needed — and
 * `deleteToken` without a registration in hand would go and register
 * Firebase's default worker instead.
 */
async function endLocalSubscription(): Promise<void> {
  if (!browserSupportsWebPush()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration(WEB_PUSH_SW_SCOPE);
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    await subscription?.unsubscribe().catch(() => false);
    await registration.unregister();
  } catch {
    // Nothing more to do locally.
  }
}

/**
 * Hand this browser's FCM token to the push service for `userId`.
 *
 * Meant to run on every load of a browser in the experiment, and cheap there:
 * a registration the service accepted for this user in the last
 * `WEB_PUSH_REFRESH_MS` is not repeated (`force` skips that — the user just
 * asked). The service keys the device by installation id, so a repeat
 * refreshes the token and its staleness clock rather than adding rows.
 *
 * A refusal (`server-disabled`, `refused`) ends the subscription it just made:
 * a browser the service will not send to must not hold one. Page loads then
 * leave it alone for `WEB_PUSH_REFUSAL_BACKOFF_MS`; `force` still asks.
 *
 * "Fresh" also needs the worker to still hold its subscription: the browser
 * can drop one on its own, and only a new registration makes another.
 */
export async function registerWebPush(options: {
  userId: string;
  /** The user just asked for it — the browser may prompt, and nothing is skipped. */
  prompt?: boolean;
  force?: boolean;
  locale?: string;
}): Promise<WebPushResult> {
  const config = webPushConfig();
  if (!config || !browserSupportsWebPush()) return { ok: false, reason: 'unsupported' };
  const force = options.force === true || options.prompt === true;

  let permission = Notification.permission;
  if (permission === 'default' && options.prompt) {
    try {
      permission = await Notification.requestPermission();
    } catch {
      permission = Notification.permission;
    }
  }
  if (permission !== 'granted') return { ok: false, reason: 'no-permission' };

  if (!force) {
    if (webPushServerRefused()) return { ok: false, reason: 'server-disabled' };
    const refusal = recentRefusal(options.userId);
    if (refusal) return { ok: false, reason: refusal };
    const last = lastRegistered();
    if (
      last &&
      last.userId === options.userId &&
      Date.now() - last.at < WEB_PUSH_REFRESH_MS &&
      (await holdsSubscription())
    ) {
      return { ok: true, fresh: true };
    }
  }

  // One registration at a time per tab. The dashboard sheet's "Enable" flips
  // the permission, which wakes the registrar too — both would mint a token
  // and POST it, and the funnel would count one "yes" twice.
  if (inFlight && inFlight.userId === options.userId) {
    return { ...(await inFlight.result), joined: true };
  }
  const result = subscribeAndRegister(config, options);
  inFlight = { userId: options.userId, result };
  try {
    return await result;
  } finally {
    if (inFlight?.result === result) inFlight = null;
  }
}

let inFlight: { userId: string; result: Promise<WebPushResult> } | null = null;

async function subscribeAndRegister(
  config: WebPushConfig,
  options: { userId: string; locale?: string },
): Promise<WebPushResult> {
  let token: string;
  try {
    const firebase = await messagingFor(config);
    if (!firebase) return { ok: false, reason: 'unsupported' };
    const serviceWorkerRegistration = await pushWorker();
    token = await firebase.module.getToken(firebase.messaging, {
      vapidKey: config.vapidKey,
      serviceWorkerRegistration,
    });
  } catch {
    return { ok: false, reason: 'no-token' };
  }
  if (typeof token !== 'string' || !token) return { ok: false, reason: 'no-token' };
  // A subscription exists from here on: mark it before asking the service, so
  // whatever the answer, it can be found and taken back.
  write(WEB_PUSH_REGISTERED_USER_KEY, options.userId);

  // The app's language, as on the phone (`pushLocale` there): a push is the
  // app speaking. The browser's only while the app has none.
  const appLanguage =
    i18n.language && i18n.language !== 'cimode' ? i18n.language : undefined;
  const locale = (options.locale ?? appLanguage ?? navigator.language)?.slice(0, 35);
  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 64);
  } catch {
    timeZone = undefined;
  }

  let res: Response;
  try {
    res = await post(PUSH_SERVICE_PATHS.devices, {
      installationId: installationId(),
      token,
      transport: 'fcm',
      platform: 'web',
      ...(locale ? { locale } : {}),
      ...(timeZone ? { timeZone } : {}),
      // The service caps it at 32; a full commit SHA is 40.
      appVersion: __APP_BUILD_ID__.slice(0, 32),
    });
  } catch {
    return { ok: false, reason: 'unreachable' };
  }
  if (!res.ok) {
    const reason = await refusalFor(res);
    if (reason === 'server-disabled' || reason === 'refused') {
      if (reason === 'server-disabled') writeSession(WEB_PUSH_SERVER_DISABLED_KEY, '1');
      write(
        WEB_PUSH_REFUSED_KEY,
        JSON.stringify({ userId: options.userId, reason, at: Date.now() }),
      );
      await endLocalSubscription();
      write(WEB_PUSH_REGISTERED_USER_KEY, null);
    }
    return { ok: false, reason };
  }
  write(
    WEB_PUSH_LAST_REGISTERED_KEY,
    JSON.stringify({ userId: options.userId, at: Date.now() }),
  );
  write(WEB_PUSH_REFUSED_KEY, null);
  writeSession(WEB_PUSH_SERVER_DISABLED_KEY, null);
  return { ok: true };
}

/**
 * Take this browser off the account, and stop it receiving anything.
 *
 * Runs on sign-out, when the experiment leaves this user, and when the browser
 * permission has been revoked. The server half goes first, while the session
 * still exists; the local half runs whatever the server said, because the FCM
 * token keeps working after the session ends — ending the subscription makes
 * FCM answer `UNREGISTERED`, and the push service prunes a row that answers
 * that, so even a registration the server call could not remove goes dead.
 */
export async function unregisterWebPush(): Promise<WebPushResult> {
  let result: WebPushResult = { ok: true };
  const id = read(WEB_PUSH_INSTALLATION_ID_KEY);
  if (id) {
    try {
      const res = await post(PUSH_SERVICE_PATHS.devicesUnregister, {
        installationId: id,
      });
      if (!res.ok) result = { ok: false, reason: await refusalFor(res) };
    } catch {
      result = { ok: false, reason: 'unreachable' };
    }
  }

  await endLocalSubscription();

  write(WEB_PUSH_REGISTERED_USER_KEY, null);
  write(WEB_PUSH_LAST_REGISTERED_KEY, null);
  return result;
}

/**
 * Sign-out's half: take this browser off the account while the session still
 * exists, if it holds anything to take back — bounded, so leaving never waits
 * on the push service. Every way out of a session goes through it (sign-out,
 * and stopping an impersonation).
 */
export async function unregisterWebPushBeforeSignOut(): Promise<void> {
  if (!hasWebPushRegistration()) return;
  await Promise.race([
    unregisterWebPush().catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, PUSH_SIGN_OUT_BUDGET_MS)),
  ]);
}
